import {showToast} from "../utils/toast.js";

const CFG = window.DOPLINE_AUTH || {};
const TRACE_KEY = "dopline_booking_trace_id";
const state = {
    user: CFG.user || {isLoggedIn: false},
    selectedHotel: null,
};

function makeTraceId() {
    const rand = Math.random().toString(36).slice(2, 10);
    return `dl-${Date.now()}-${rand}`;
}

function ensureTraceId(hotel) {
    let traceId = hotel?.traceId || "";
    try {
        traceId = traceId || sessionStorage.getItem(TRACE_KEY) || "";
    } catch (_) {}
    if (!traceId) traceId = makeTraceId();
    if (hotel && !hotel.traceId) hotel.traceId = traceId;
    try {
        sessionStorage.setItem(TRACE_KEY, traceId);
    } catch (_) {}
    return traceId;
}

function qs(sel, root = document) {
    return root.querySelector(sel);
}

function qsa(sel, root = document) {
    return Array.from(root.querySelectorAll(sel));
}

function setMessage(node, text, ok = false) {
    if (!node) return;
    node.textContent = text || "";
    node.classList.toggle("is-success", !!ok);
    node.classList.toggle("is-error", !!text && !ok);
}

function setButtonLoading(btn, loading) {
    if (!btn) return;
    btn.classList.toggle("is-loading", !!loading);
    btn.disabled = !!loading;
    if (!btn.dataset.originalText) {
        btn.dataset.originalText = (btn.textContent || "").trim();
    }

    if (loading) {
        const loadingText = (btn.dataset.loadingText || "Загрузка...").trim();
        if (loadingText !== "") {
            btn.textContent = loadingText;
        }
    } else if (btn.dataset.originalText) {
        btn.textContent = btn.dataset.originalText;
    }
}

function post(action, payload = {}) {
    const body = new URLSearchParams();
    body.set("action", action);
    body.set("nonce", CFG.nonce || "");
    Object.entries(payload).forEach(([k, v]) => {
        if (Array.isArray(v) || (v && typeof v === "object")) {
            body.set(k, JSON.stringify(v));
        } else {
            body.set(k, v == null ? "" : String(v));
        }
    });

    return fetch(CFG.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"},
        body: body.toString(),
    }).then((r) => r.json());
}

function buildFormData(form) {
    const fd = new FormData(form);
    const obj = {};
    for (const [k, v] of fd.entries()) {
        obj[k] = v;
    }
    return obj;
}

const ACTION_MAP = {
    login: "dopline_auth_login",
    register: "dopline_auth_register",
    forgot: "dopline_auth_forgot_password",
    reset: "dopline_auth_reset_password",
};

// Панели, для которых не нужна перезагрузка страницы после успеха
const NO_RELOAD_PANELS = new Set(["forgot", "reset"]);

// Панели, скрывающие вкладки (вход/регистрация)
const HIDE_TABS_PANELS = new Set(["forgot", "reset"]);

function initAuthModal() {
    const root = qs("[data-dopline-auth]");
    if (!root) return;

    const tabs = qsa("[data-dopline-auth-tab]", root);
    const tabsContainer = qs("[data-dopline-auth-tabs]", root);
    const forms = qsa("[data-dopline-auth-form]", root);
    const closeButtons = qsa("[data-dopline-auth-close]", root);
    const messageNode = qs("[data-dopline-auth-message]", root);
    const logoutBtn = qs("[data-dopline-auth-logout]", root);

    function show(open) {
        root.hidden = !open;
        if (!open) {
            setMessage(messageNode, "");
        }
    }

    function setTab(tab) {
        tabs.forEach((t) => t.classList.toggle("is-active", t.dataset.doplineAuthTab === tab));
        forms.forEach((f) => f.classList.toggle("is-active", f.dataset.doplineAuthForm === tab));
        if (tabsContainer) {
            tabsContainer.classList.toggle("is-hidden", HIDE_TABS_PANELS.has(tab));
        }
        setMessage(messageNode, "");
    }

    // Обработчики переключения панелей (вкладки + ссылки forgot/back)
    qsa("[data-dopline-auth-tab]", root).forEach((btn) => {
        btn.addEventListener("click", () => setTab(btn.dataset.doplineAuthTab || "login"));
    });

    closeButtons.forEach((btn) => btn.addEventListener("click", () => show(false)));

    qsa('[data-dopline-auth-open="auth"]').forEach((btn) => {
        btn.addEventListener("click", () => show(true));
    });

    // Проверяем URL-параметры для авто-открытия формы сброса пароля
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("dopline_reset") === "1") {
        const resetKey = urlParams.get("key") || "";
        const resetLogin = urlParams.get("login") || "";
        if (resetKey && resetLogin) {
            const resetForm = qs('[data-dopline-auth-form="reset"]', root);
            if (resetForm) {
                const keyInput = qs('[name="key"]', resetForm);
                const loginInput = qs('[name="login"]', resetForm);
                if (keyInput) keyInput.value = resetKey;
                if (loginInput) loginInput.value = resetLogin;
            }
            setTab("reset");
            show(true);
        }
    }

    forms.forEach((form) => {
        form.addEventListener("submit", async (e) => {
            e.preventDefault();
            const type = form.dataset.doplineAuthForm;
            const payload = buildFormData(form);
            const action = ACTION_MAP[type] || "dopline_auth_login";

            setMessage(messageNode, "");
            const submit = qs('button[type="submit"]', form);
            setButtonLoading(submit, true);

            try {
                const res = await post(action, payload);
                if (!res?.success) {
                    setMessage(messageNode, res?.data?.message || "Ошибка");
                    return;
                }

                if (NO_RELOAD_PANELS.has(type)) {
                    // Для forgot — показываем сообщение и сбрасываем форму
                    // Для reset — переключаемся на вход с сообщением об успехе
                    form.reset();
                    if (type === "reset") {
                        // Убираем reset-параметры из адресной строки
                        const cleanUrl = window.location.pathname;
                        window.history.replaceState({}, "", cleanUrl);
                        setTab("login");
                    }
                    setMessage(messageNode, res?.data?.message || "Готово", true);
                    return;
                }

                setMessage(messageNode, "Успешно", true);
                form.reset();
                show(false);

                const currentUrl = window.location.href;
                setTimeout(() => {
                    window.location.href = currentUrl;
                }, 500);
            } catch (err) {
                setMessage(messageNode, "Ошибка сети");
            } finally {
                setButtonLoading(submit, false);
            }
        });
    });

    logoutBtn?.addEventListener("click", async () => {
        setMessage(messageNode, "");
        setButtonLoading(logoutBtn, true);
        try {
            const res = await post("dopline_auth_logout", {});
            if (!res?.success) {
                setMessage(messageNode, res?.data?.message || "Не удалось выйти");
                return;
            }

            state.user = res.data?.user || {isLoggedIn: false};
            setMessage(messageNode, "Вы вышли из аккаунта", true);
        } catch (err) {
            setMessage(messageNode, "Ошибка сети");
        } finally {
            setButtonLoading(logoutBtn, false);
        }
    });

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
            show(false);
        }
    });

    window.DoplineAuthModal = {
        open: () => show(true),
        close: () => show(false),
        setTab,
    };
}

function readHotelFromCard(card) {
    const rawRules = card.dataset.bookCancellationRules || "[]";
    let cancellationRules = [];
    try {
        cancellationRules = JSON.parse(rawRules);
        if (!Array.isArray(cancellationRules)) cancellationRules = [];
    } catch (e) {
        cancellationRules = [];
    }

    const listWrap = card.closest("[data-results-list-wrap]");
    const nights = Number(listWrap?.dataset?.nights || 1);

    return {
        id: card.dataset.hotelId || "",
        title: card.dataset.title || "Отель",
        hs: card.dataset.hs || "",
        rateId: card.dataset.bookRateId || "",
        price: Number(card.dataset.bookPrice || 0),
        isNonRefundable: Number(card.dataset.bookIsNonRefundable || 0) ? 1 : 0,
        partnerFee: Number(card.dataset.bookPartnerFee || 0),
        agentFee: Number(card.dataset.bookAgentFee || 0),
        freeCancellationBefore: card.dataset.bookFreeCancellationBefore || "",
        cancellationRules,
        adults: Number(card.dataset.searchAdults || 1),
        children: Number(card.dataset.searchChildren || 0),
        nights,
    };
}

function guestRow(index, type) {
    const label = type === "chd" ? `Ребенок ${index + 1}` : `Взрослый ${index + 1}`;
    const ageInput = type === "chd"
        ? `<input class="dl-input" type="number" min="0" max="17" name="guest_${index}_age" value="7" placeholder="Возраст (0-17)" required>`
        : "";

    return `
            <div class="dopline-booking__guest" data-type="${type}">
                <div class="dopline-booking__guest-title">${label}</div>
                <div class="dopline-booking__guest-grid">
                    <input class="dl-input" type="text" name="guest_${index}_first_name" placeholder="Имя (латиницей)" required>
                    <input class="dl-input" type="text" name="guest_${index}_last_name" placeholder="Фамилия (латиницей)" required>
                    ${ageInput}
                </div>
            </div>
        `;
}

function collectGuests(form, hotel) {
    const guests = [];
    let idx = 0;

    for (let i = 0; i < hotel.adults; i++, idx++) {
        guests.push({
            type: "adt",
            first_name: form.querySelector(`[name="guest_${idx}_first_name"]`)?.value?.trim() || "",
            last_name: form.querySelector(`[name="guest_${idx}_last_name"]`)?.value?.trim() || "",
        });
    }

    for (let i = 0; i < hotel.children; i++, idx++) {
        guests.push({
            type: "chd",
            first_name: form.querySelector(`[name="guest_${idx}_first_name"]`)?.value?.trim() || "",
            last_name: form.querySelector(`[name="guest_${idx}_last_name"]`)?.value?.trim() || "",
            age: Number(form.querySelector(`[name="guest_${idx}_age"]`)?.value || 7),
        });
    }

    return guests;
}

function initBookingModal() {
    const root = qs("[data-dopline-booking]");
    if (!root) return;

    const form = qs("[data-dopline-booking-form]", root);
    const title = qs("[data-dopline-booking-title]", root);
    const roomNode = qs("[data-dopline-booking-room]", root);
    const subtitle = qs("[data-dopline-booking-subtitle]", root);
    const guestsWrap = qs("[data-dopline-booking-guests]", root);
    const messageNode = qs("[data-dopline-booking-message]", root);
    const closeButtons = qsa("[data-dopline-booking-close]", root);
    const successBlock = qs("[data-dopline-booking-success]", root);
    const successText = qs("[data-dopline-booking-success-text]", root);
    const dashboardLink = qs("[data-dopline-booking-dashboard-link]", root);

    if (dashboardLink && CFG.dashboardUrl) {
        dashboardLink.href = CFG.dashboardUrl;
    }

    function showSuccess(billingNumber) {
        if (form) form.hidden = true;
        if (messageNode) messageNode.textContent = "";
        if (successText) {
            const num = billingNumber ? ` №${billingNumber}` : "";
            successText.textContent = `Ваше бронирование${num} успешно создано. Детали и статус заказа доступны в личном кабинете.`;
        }
        if (successBlock) successBlock.hidden = false;
    }

    function resetToForm() {
        if (successBlock) successBlock.hidden = true;
        if (form) form.hidden = false;
        setMessage(messageNode, "");
    }

    function show(open) {
        root.hidden = !open;
        if (!open) {
            resetToForm();
        }
    }

    closeButtons.forEach((btn) => btn.addEventListener("click", () => show(false)));

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") show(false);
    });

    window.openBooking = function (hotel) {
        ensureTraceId(hotel);
        state.selectedHotel = hotel;
        resetToForm();
        const totalGuests = Math.max(1, hotel.adults + hotel.children);

        if (title) title.textContent = `Бронирование: ${hotel.title}`;
        if (roomNode) roomNode.textContent = hotel.roomName || "";
        if (subtitle) {
            const dc = window.doplineCurrency;
            const priceFmt = Number.isFinite(hotel.price)
                ? (dc ? dc.format(hotel.price) : hotel.price.toLocaleString("ru-RU") + " ₽")
                : "0 ₽";
            subtitle.textContent = `Тариф: ${priceFmt}, гостей: ${totalGuests}`;
        }

        setMessage(messageNode, "");

        let rows = "";
        let idx = 0;
        for (let i = 0; i < hotel.adults; i++, idx++) rows += guestRow(idx, "adt");
        for (let i = 0; i < hotel.children; i++, idx++) rows += guestRow(idx, "chd");
        guestsWrap.innerHTML = rows;

        const emailInput = qs('[name="client_email"]', form);
        if (emailInput && state.user?.email) emailInput.value = state.user.email;

        show(true);
    };

    async function runCheckRate(hotel) {
        const CFG = window.DOPLINE_AUTH || {};
        const checkRateUrl = (CFG.restUrl || "/wp-json") + "/dopline/v1/myagent/hotel/check-rate";
        const traceId = ensureTraceId(hotel);

        const body = {
            hs: hotel.hs,
            hotel_id: hotel.id,
            rate_id: hotel.rateId,
            price: hotel.price,
            display_price: hotel.price,
            partner_affiliate_fee: hotel.partnerFee,
            agent_affiliate_fee: hotel.agentFee,
            lang: "ru",
            is_non_refundable: hotel.isNonRefundable,
            free_cancellation_before: hotel.freeCancellationBefore || "",
            cancellation_rules: hotel.cancellationRules || [],
            trace_id: traceId,
        };

        const resp = await fetch(checkRateUrl, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": CFG.restNonce || "",
                "X-Dopline-Trace-Id": traceId,
            },
            body: JSON.stringify(body),
        });

        // Always parse JSON so error bodies (410 rate_expired, 500) are accessible.
        return resp.json().catch(() => null);
    }

    form?.addEventListener("submit", async (e) => {
        e.preventDefault();
        if (!state.selectedHotel) return;

        const hotel = state.selectedHotel;
        const submit = qs('button[type="submit"]', form);
        setButtonLoading(submit, true);
        setMessage(messageNode, "");

        // Step 1: check-rate (mandatory per API docs before booking).
        try {
            const crRes = await runCheckRate(hotel);
            // rate_expired (code 7 or 1022 mapped to 410 by our REST layer) — hard block.
            if (crRes?.code === "rate_expired") {
                setMessage(messageNode, crRes.message || "К сожалению, этот тариф уже не актуален. Пожалуйста, выполните поиск заново.");
                setButtonLoading(submit, false);
                return;
            }
            if (crRes?.success && crRes?.data) {
                const priceChanges = crRes.data.check_price_changes;
                if (priceChanges && priceChanges.new && priceChanges.old) {
                    const newPrice = Number(priceChanges.new.price);
                    const currentPrice = Number(hotel.price || 0);
                    if (Number.isFinite(newPrice) && newPrice > 0 && Math.round(newPrice) !== Math.round(currentPrice)) {
                        const newPartnerFee = Number(priceChanges.new.partnerAffiliateFee);
                        // Update the price in state so booking uses the actual price
                        state.selectedHotel = {
                            ...hotel,
                            price: newPrice,
                            partnerFee: Number.isFinite(newPartnerFee) ? newPartnerFee : hotel.partnerFee,
                        };
                        const dc = window.doplineCurrency;
                        const fmtP = (p) => dc ? dc.format(p) : p.toLocaleString("ru-RU") + " ₽";
                        const priceMsg = `Цена изменилась: ${fmtP(currentPrice)} → ${fmtP(newPrice)}. Бронируем по новой цене.`;
                        setMessage(messageNode, priceMsg, false);
                    }
                }
            }
        } catch (_) {
            // Network failure is non-fatal — proceed with booking.
        }

        const currentHotel = state.selectedHotel;
        const guests = collectGuests(form, currentHotel);

        const latinOnly = /^[A-Za-z\s.-]+$/;
        const invalidGuest = guests.find(
            (g) => !latinOnly.test(g.first_name) || !latinOnly.test(g.last_name)
        );
        if (invalidGuest) {
            setMessage(messageNode, "Имена гостей должны быть введены латинскими буквами (например: Ivan Petrov).");
            setButtonLoading(submit, false);
            return;
        }

        const payload = {
            hotel_id: currentHotel.id,
            hs: currentHotel.hs,
            rate_id: currentHotel.rateId,
            price: currentHotel.price,
            is_non_refundable: currentHotel.isNonRefundable,
            partner_affiliate_fee: currentHotel.partnerFee,
            agent_affiliate_fee: currentHotel.agentFee,
            free_cancellation_before: currentHotel.freeCancellationBefore,
            client_email: qs('[name="client_email"]', form)?.value || "",
            client_phone: qs('[name="client_phone"]', form)?.value || "",
            trace_id: ensureTraceId(currentHotel),
        };

        guests.forEach((guest, i) => {
            payload[`guests[${i}][first_name]`] = guest.first_name;
            payload[`guests[${i}][last_name]`] = guest.last_name;
            payload[`guests[${i}][type]`] = guest.type;
            if (guest.type === "chd") {
                payload[`guests[${i}][age]`] = String(guest.age);
            }
        });

        (currentHotel.cancellationRules || []).forEach((rule, i) => {
            payload[`cancellation_policy_rules[${i}][isPossible]`] = rule.isPossible ? "1" : "0";
            payload[`cancellation_policy_rules[${i}][amount]`] = String(rule.amount || 0);
            payload[`cancellation_policy_rules[${i}][UTCDateFrom]`] = rule.UTCDateFrom || "";
            payload[`cancellation_policy_rules[${i}][UTCDateTo]`] = rule.UTCDateTo || "";
        });

        try {
            const res = await post("dopline_auth_book_hotel", payload);
            if (!res?.success) {
                setMessage(messageNode, res?.data?.message || "Не удалось создать бронирование");
                return;
            }

            const order = res.data?.order || {};
            showSuccess(order.billing_number || null);
        } catch (err) {
            setMessage(messageNode, "Ошибка сети");
        } finally {
            setButtonLoading(submit, false);
        }
    });

    qsa("[data-dopline-book]").forEach((btn) => {
        btn.addEventListener("click", () => {
            const card = btn.closest(".dopline-hotel");
            if (!card) return;

            const hotel = readHotelFromCard(card);
            ensureTraceId(hotel);

            if (!hotel.id || !hotel.hs || !hotel.rateId || !hotel.price) {
                showToast("Для выбранного отеля нет валидного тарифа для бронирования.", "error");
                return;
            }

            if (!state.user?.isLoggedIn) {
                window.DoplineAuthModal?.setTab("login");
                window.DoplineAuthModal?.open();
                return;
            }

            try {
                sessionStorage.setItem("dopline_booking_data", JSON.stringify(hotel));
                sessionStorage.setItem(TRACE_KEY, hotel.traceId || "");
            } catch (_) {}

            const bookingPageUrl = CFG.bookingPageUrl || window.location.origin + "/hotel-booking/";
            window.location.href = bookingPageUrl;
        });
    });
}

export function initAuthBooking() {
    if (!CFG.ajaxUrl) return;
    initAuthModal();
    // Booking modal replaced by dedicated booking page (template-hotel-booking.php)
    // initBookingModal() is kept but not called; the [data-dopline-book] handler
    // inside initBookingModal now redirects to the booking page.
    initBookingModal();
}
